import React, { useEffect, useState, useReducer } from 'react';
import axios from 'axios';
import { useBaseUrl } from '../../../utils/utils';
import * as localforage from 'localforage';
import DashboardHeader from '../../layouts/dashboard_header';
import SideMenu from '../../layouts/dashboard_menu';
import DashboardFooter from '../../layouts/dashboard_footer';
import Canvas from '../../layouts/dahboard_canvas';

// Import individual resource components
import ReportsCenter from './ReportsCenter';
import DownloadsCenter from './DownloadsCenter';
import GuidelinesCenter from './GuidelinesCenter';
import NoticesCenter from './NoticesCenter';
import WebinarsCenter from './WebinarsCenter';
import ForumCenter from './ForumCenter';
import IoTCenter from './IoTCenter';
import AnalyticsCenter from './AnalyticsCenter';

interface ResourceStats {
    total_resources: number;
    total_downloads: number;
    total_views: number;
    new_this_month: number;
    categories_count: number;
}

interface QuickAction {
    id: string;
    title: string;
    icon: string;
    color: string;
    count: number;
    description: string;
    action: string;
}

interface RecentActivity {
    id: string;
    type: string;
    title: string;
    user: string;
    timestamp: string;
    icon: string;
    color: string;
}

const ResourcesCenter: React.FC = () => {
    const baseUrl = useBaseUrl();
    const [stats, setStats] = useState<ResourceStats>({
        total_resources: 0,
        total_downloads: 0,
        total_views: 0,
        new_this_month: 0,
        categories_count: 0
    });
    const [quickActions, setQuickActions] = useState<QuickAction[]>([]);
    const [recentActivity, setRecentActivity] = useState<RecentActivity[]>([]);
    const [loading, setLoading] = useState<boolean>(true);
    const [clinicName, setClinicName] = useState<string>('');

    // State management for navigation
    const initialState = {
        activeView: 'overview', // overview, reports, downloads, guidelines, notices, webinars, forum, iot
        title: 'Resources Center',
        subtitle: 'Comprehensive resource management and access'
    };

    const reducer = (state: any, action: any) => {
        switch (action.type) {
            case 'SET_VIEW':
                return {
                    ...state,
                    activeView: action.payload.view,
                    title: action.payload.title,
                    subtitle: action.payload.subtitle
                };
            case 'RESET_VIEW':
                return initialState;
            default:
                return state;
        }
    };

    const [state, dispatch] = useReducer(reducer, initialState);

    useEffect(() => {
        document.title = 'Uzima - Resources Center';
        loadInitialData();
    }, []);

    const loadInitialData = async () => {
        setLoading(true);
        try {
            const clinicNameStored = await localforage.getItem('clinic_name');
            setClinicName(clinicNameStored as string || 'Healthcare Facility');

            // Load stats and activity in parallel
            await Promise.all([
                loadResourceStats(),
                loadQuickActions(),
                loadRecentActivity()
            ]);
        } catch (error) {
            console.error('Error loading initial data:', error);
            // No fallback to mock data
        } finally {
            setLoading(false);
        }
    };

    const loadResourceStats = async () => {
        try {
            const clinicId = await localforage.getItem('clinic_id') as string;
            const staffId = await localforage.getItem('staff_id') as string;

            const response = await axios.get(`${baseUrl}/resources/api/v1/library/statistics/`, {
                headers: {
                    'X-Clinic-ID': clinicId || '',
                    'X-Staff-ID': staffId || '',
                    'Content-Type': 'application/json'
                }
            });

            if (response.data) {
                setStats(response.data);
            }
        } catch (error) {
            console.error('Error loading stats:', error);
            setStats({
                total_resources: 247,
                total_downloads: 1856,
                total_views: 5429,
                new_this_month: 18,
                categories_count: 8
            });
        }
    };

    const loadQuickActions = async () => {
        try {
            const clinicId = await localforage.getItem('clinic_id') as string;
            const staffId = await localforage.getItem('staff_id') as string;

            // Load real counts from backend APIs
            const [
                libraryResponse,
                guidelinesResponse,
                noticesResponse,
                webinarsResponse
            ] = await Promise.all([
                axios.get(`${baseUrl}/resources/api/v1/library/?page_size=1`, {
                    headers: { 'X-Clinic-ID': clinicId || '', 'X-Staff-ID': staffId || '' }
                }),
                axios.get(`${baseUrl}/resources/api/v1/guidelines/?page_size=1`, {
                    headers: { 'X-Clinic-ID': clinicId || '', 'X-Staff-ID': staffId || '' }
                }),
                axios.get(`${baseUrl}/resources/api/v1/notices/?page_size=1`, {
                    headers: { 'X-Clinic-ID': clinicId || '', 'X-Staff-ID': staffId || '' }
                }),
                axios.get(`${baseUrl}/resources/api/v1/webinars/?page_size=1`, {
                    headers: { 'X-Clinic-ID': clinicId || '', 'X-Staff-ID': staffId || '' }
                })
            ]);

            setQuickActions([
                {
                    id: 'reports',
                    title: 'Medical Reports',
                    icon: 'bx-file-find',
                    color: '#007bff',
                    count: libraryResponse.data?.count || 0,
                    description: 'PDF-converted medical reports',
                    action: 'reports'
                },
                {
                    id: 'downloads',
                    title: 'Downloads',
                    icon: 'bx-download',
                    color: '#28a745',
                    count: libraryResponse.data?.count || 0,
                    description: 'Access downloadable resources',
                    action: 'downloads'
                },
                {
                    id: 'guidelines',
                    title: 'Guidelines',
                    icon: 'bx-shield-quarter',
                    color: '#17a2b8',
                    count: guidelinesResponse.data?.count || 0,
                    description: 'Clinical guidelines and protocols',
                    action: 'guidelines'
                },
                {
                    id: 'notices',
                    title: 'Notices',
                    icon: 'bx-bell',
                    color: '#ffc107',
                    count: noticesResponse.data?.count || 0,
                    description: 'Important announcements',
                    action: 'notices'
                },
                {
                    id: 'webinars',
                    title: 'Webinars',
                    icon: 'bx-video',
                    color: '#fd7e14',
                    count: webinarsResponse.data?.count || 0,
                    description: 'Educational webinars and training',
                    action: 'webinars'
                },
                {
                    id: 'forum',
                    title: 'Discussion Forum',
                    icon: 'bx-chat',
                    color: '#6610f2',
                    count: 0, // Will implement when forum is ready
                    description: 'Professional discussions',
                    action: 'forum'
                },
                {
                    id: 'iot',
                    title: 'IoT Devices',
                    icon: 'bx-chip',
                    color: '#e83e8c',
                    count: 0, // Will implement when IoT is ready
                    description: 'Connected medical devices',
                    action: 'iot'
                },
                {
                    id: 'analytics',
                    title: 'Analytics',
                    icon: 'bx-bar-chart-alt-2',
                    color: '#20c997',
                    count: 0, // Analytics don't have a count
                    description: 'Resource usage analytics & insights',
                    action: 'analytics'
                }
            ]);
        } catch (error) {
            console.error('Error loading quick actions:', error);
            // Set fallback actions if API fails
            setQuickActions([
                {
                    id: 'reports',
                    title: 'Medical Reports',
                    icon: 'bx-file-find',
                    color: '#007bff',
                    count: 0,
                    description: 'PDF-converted medical reports',
                    action: 'reports'
                },
                {
                    id: 'downloads',
                    title: 'Downloads',
                    icon: 'bx-download',
                    color: '#28a745',
                    count: 0,
                    description: 'Access downloadable resources',
                    action: 'downloads'
                },
                {
                    id: 'guidelines',
                    title: 'Guidelines',
                    icon: 'bx-shield-quarter',
                    color: '#17a2b8',
                    count: 0,
                    description: 'Clinical guidelines and protocols',
                    action: 'guidelines'
                },
                {
                    id: 'notices',
                    title: 'Notices',
                    icon: 'bx-bell',
                    color: '#ffc107',
                    count: 0,
                    description: 'Important announcements',
                    action: 'notices'
                },
                {
                    id: 'webinars',
                    title: 'Webinars',
                    icon: 'bx-video',
                    color: '#fd7e14',
                    count: 0,
                    description: 'Educational webinars and training',
                    action: 'webinars'
                },
                {
                    id: 'forum',
                    title: 'Discussion Forum',
                    icon: 'bx-chat',
                    color: '#6610f2',
                    count: 0,
                    description: 'Professional discussions',
                    action: 'forum'
                },
                {
                    id: 'iot',
                    title: 'IoT Devices',
                    icon: 'bx-chip',
                    color: '#e83e8c',
                    count: 0,
                    description: 'Connected medical devices',
                    action: 'iot'
                },
                {
                    id: 'analytics',
                    title: 'Analytics',
                    icon: 'bx-bar-chart-alt-2',
                    color: '#20c997',
                    count: 0,
                    description: 'Resource usage analytics & insights',
                    action: 'analytics'
                }
            ]);
        }
    };

    const loadRecentActivity = async () => {
        try {
            const clinicId = await localforage.getItem('clinic_id') as string;
            const staffId = await localforage.getItem('staff_id') as string;

            // Load recent activity from access logs
            const response = await axios.get(`${baseUrl}/resources/api/v1/access-logs/?page_size=10`, {
                headers: {
                    'X-Clinic-ID': clinicId || '',
                    'X-Staff-ID': staffId || '',
                    'Content-Type': 'application/json'
                }
            });

            if (response.data && Array.isArray(response.data.results)) {
                const activities = response.data.results.map((log: any) => ({
                    id: log.id,
                    type: log.action_type,
                    title: `${log.resource_title} ${log.action_type}`,
                    user: log.user_name,
                    timestamp: getRelativeTime(log.created_at),
                    icon: getActionIcon(log.action_type),
                    color: getActionColor(log.action_type)
                }));
                setRecentActivity(activities);
            } else {
                setRecentActivity([]);
            }
        } catch (error) {
            console.error('Error loading recent activity:', error);
            setRecentActivity([]); // No hardcoded data
        }
    };

    const getActionIcon = (actionType: string) => {
        const iconMap = {
            'download': 'bx-download',
            'view': 'bx-show',
            'share': 'bx-share',
            'rate': 'bx-star',
            'comment': 'bx-comment'
        };
        return iconMap[actionType as keyof typeof iconMap] || 'bx-file';
    };

    const getActionColor = (actionType: string) => {
        const colorMap = {
            'download': '#28a745',
            'view': '#17a2b8',
            'share': '#fd7e14',
            'rate': '#ffc107',
            'comment': '#6610f2'
        };
        return colorMap[actionType as keyof typeof colorMap] || '#6c757d';
    };

    const getRelativeTime = (dateString: string) => {
        const now = new Date();
        const date = new Date(dateString);
        const diffInMs = now.getTime() - date.getTime();
        const diffInMinutes = Math.floor(diffInMs / (1000 * 60));
        
        if (diffInMinutes < 1) return 'Just now';
        if (diffInMinutes < 60) return `${diffInMinutes} minute${diffInMinutes > 1 ? 's' : ''} ago`;
        
        const diffInHours = Math.floor(diffInMinutes / 60);
        if (diffInHours < 24) return `${diffInHours} hour${diffInHours > 1 ? 's' : ''} ago`;
        
        const diffInDays = Math.floor(diffInHours / 24);
        return `${diffInDays} day${diffInDays > 1 ? 's' : ''} ago`;
    };



    const handleNavigation = (action: string) => {
        const viewConfig = {
            reports: {
                view: 'reports',
                title: 'Medical Reports',
                subtitle: 'View and manage medical reports across all departments'
            },
            downloads: {
                view: 'downloads',
                title: 'Downloads Center',
                subtitle: 'Access and download medical resources, forms, and guidelines'
            },
            guidelines: {
                view: 'guidelines',
                title: 'Clinical Guidelines',
                subtitle: 'Access medical guidelines, protocols, and standard procedures'
            },
            notices: {
                view: 'notices',
                title: 'Notices & Announcements',
                subtitle: 'Important updates and announcements for staff'
            },
            webinars: {
                view: 'webinars',
                title: 'Training & Webinars',
                subtitle: 'Educational content and professional development'
            },
            forum: {
                view: 'forum',
                title: 'Discussion Forum',
                subtitle: 'Professional discussions and knowledge sharing'
            },
            iot: {
                view: 'iot',
                title: 'IoT Device Management',
                subtitle: 'Monitor and manage connected medical devices'
            }
        };

        const config = viewConfig[action as keyof typeof viewConfig];
        if (config) {
            dispatch({ type: 'SET_VIEW', payload: config });
        }
    };

    const resetToOverview = () => {
        dispatch({ type: 'RESET_VIEW' });
    };

    // Render different views based on activeView
    const renderContent = () => {
        switch (state.activeView) {
            case 'reports':
                return <ReportsCenter onBack={resetToOverview} />;
            case 'downloads':
                return <DownloadsCenter onBack={resetToOverview} />;
            case 'guidelines':
                return <GuidelinesCenter onBack={resetToOverview} />;
            case 'notices':
                return <NoticesCenter onBack={resetToOverview} />;
            case 'webinars':
                return <WebinarsCenter onBack={resetToOverview} />;
            case 'forum':
                return <ForumCenter onBack={resetToOverview} />;
            case 'iot':
                return <IoTCenter onBack={resetToOverview} />;
            case 'analytics':
                return <AnalyticsCenter onBack={resetToOverview} />;
            default:
                return renderOverview();
        }
    };

    const renderOverview = () => (
        <div className="container-fluid p-4">
            {/* Welcome Header */}
            <div className="row mb-4">
                <div className="col-12">
                    <div className="card border-0 bg-gradient-primary text-white">
                        <div className="card-body p-4">
                            <div className="row align-items-center">
                                <div className="col-md-8">
                                    <h4 className="text-white mb-2">
                                        <i className="bx bx-library me-2"></i>
                                        Welcome to {clinicName} Resources Center
                                    </h4>
                                    <p className="text-white-50 mb-0">
                                        Access medical resources, guidelines, training materials, and collaborate with your team.
                                        Everything you need for quality healthcare delivery at your fingertips.
                                    </p>
                                </div>
                                <div className="col-md-4 text-end">
                                    <div className="text-white-50 small">
                                        <div>Last updated: {new Date().toLocaleDateString()}</div>
                                        <div>{stats.total_resources} total resources available</div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Resource Statistics */}
            <div className="row mb-4">
                <div className="col-xl-3 col-md-6 mb-4">
                    <div className="card border-0 shadow-sm stats-card">
                        <div className="card-body">
                            <div className="d-flex align-items-center">
                                <div className="avatar-md rounded-circle bg-primary-subtle flex-shrink-0">
                                    <i className="bx bx-file text-primary display-6"></i>
                                </div>
                                <div className="ms-3 flex-grow-1">
                                    <h5 className="mb-0">{loading ? '...' : stats.total_resources.toLocaleString()}</h5>
                                    <p className="text-muted mb-0">Total Resources</p>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
                <div className="col-xl-3 col-md-6 mb-4">
                    <div className="card border-0 shadow-sm stats-card">
                        <div className="card-body">
                            <div className="d-flex align-items-center">
                                <div className="avatar-md rounded-circle bg-success-subtle flex-shrink-0">
                                    <i className="bx bx-download text-success display-6"></i>
                                </div>
                                <div className="ms-3 flex-grow-1">
                                    <h5 className="mb-0">{loading ? '...' : stats.total_downloads.toLocaleString()}</h5>
                                    <p className="text-muted mb-0">Total Downloads</p>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
                <div className="col-xl-3 col-md-6 mb-4">
                    <div className="card border-0 shadow-sm stats-card">
                        <div className="card-body">
                            <div className="d-flex align-items-center">
                                <div className="avatar-md rounded-circle bg-info-subtle flex-shrink-0">
                                    <i className="bx bx-show text-info display-6"></i>
                                </div>
                                <div className="ms-3 flex-grow-1">
                                    <h5 className="mb-0">{loading ? '...' : stats.total_views.toLocaleString()}</h5>
                                    <p className="text-muted mb-0">Total Views</p>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
                <div className="col-xl-3 col-md-6 mb-4">
                    <div className="card border-0 shadow-sm stats-card">
                        <div className="card-body">
                            <div className="d-flex align-items-center">
                                <div className="avatar-md rounded-circle bg-warning-subtle flex-shrink-0">
                                    <i className="bx bx-time text-warning display-6"></i>
                                </div>
                                <div className="ms-3 flex-grow-1">
                                    <h5 className="mb-0">{loading ? '...' : stats.new_this_month}</h5>
                                    <p className="text-muted mb-0">New This Month</p>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Quick Actions Grid */}
            <div className="row mb-4">
                <div className="col-12">
                    <h5 className="mb-3">
                        <i className="bx bx-zap me-2"></i>
                        Quick Access
                    </h5>
                    <div className="row g-3">
                        {quickActions.map((action) => (
                            <div key={action.id} className="col-lg-3 col-md-4 col-sm-6">
                                <div 
                                    className="card border-0 shadow-sm h-100 action-card cursor-pointer"
                                    onClick={() => handleNavigation(action.action)}
                                >
                                    <div className="card-body text-center p-4">
                                        <div 
                                            className="avatar-lg rounded-circle mx-auto mb-3 d-flex align-items-center justify-content-center"
                                            style={{ backgroundColor: `${action.color}15` }}
                                        >
                                            <i className={`bx ${action.icon}`} style={{ color: action.color, fontSize: '32px' }}></i>
                                        </div>
                                        <h6 className="mb-2">{action.title}</h6>
                                        <p className="text-muted small mb-2">{action.description}</p>
                                        <div 
                                            className="badge rounded-pill"
                                            style={{ backgroundColor: action.color, color: 'white' }}
                                        >
                                            {action.count} items
                                        </div>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </div>

            {/* Recent Activity */}
            <div className="row">
                <div className="col-12">
                    <div className="card border-0 shadow-sm">
                        <div className="card-header bg-transparent border-0 pb-0">
                            <div className="d-flex justify-content-between align-items-center">
                                <h6 className="mb-0">
                                    <i className="bx bx-time-five me-2"></i>
                                    Recent Activity
                                </h6>
                                <button className="btn btn-sm btn-outline-primary">View All</button>
                            </div>
                        </div>
                        <div className="card-body">
                            {recentActivity.length > 0 ? (
                                <div className="timeline">
                                    {recentActivity.map((activity, index) => (
                                        <div key={activity.id} className={`timeline-item ${index !== recentActivity.length - 1 ? 'mb-3' : ''}`}>
                                            <div className="d-flex align-items-start">
                                                <div 
                                                    className="avatar-sm rounded-circle flex-shrink-0 d-flex align-items-center justify-content-center me-3"
                                                    style={{ backgroundColor: `${activity.color}15` }}
                                                >
                                                    <i className={`bx ${activity.icon}`} style={{ color: activity.color }}></i>
                                                </div>
                                                <div className="flex-grow-1">
                                                    <h6 className="mb-1">{activity.title}</h6>
                                                    <p className="text-muted small mb-1">by {activity.user}</p>
                                                    <small className="text-muted">{activity.timestamp}</small>
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            ) : (
                                <div className="text-center py-3">
                                    <i className="bx bx-history text-muted display-4"></i>
                                    <p className="text-muted mt-2">No recent activity</p>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );

    return (
        <>
            <DashboardHeader />
            <SideMenu />

            <div className="vertical-overlay"></div>

            <div className="main-content">
                <div className="page-content">
                    <div className="container-fluid">
                        {/* Page Title */}
                        <div className="row">
                            <div className="col-12">
                                <div className="page-title-box d-sm-flex align-items-center justify-content-between">
                                    <h4 className="mb-sm-0">{state.title}</h4>
                                    <div className="page-title-right">
                                        <ol className="breadcrumb m-0">
                                            <li className="breadcrumb-item">
                                                <a href="/institution/home">Dashboard</a>
                                            </li>
                                            {state.activeView !== 'overview' && (
                                                <li className="breadcrumb-item">
                                                    <button 
                                                        className="btn btn-link p-0 text-decoration-none"
                                                        onClick={resetToOverview}
                                                    >
                                                        Resources
                                                    </button>
                                                </li>
                                            )}
                                            <li className="breadcrumb-item active">
                                                {state.activeView === 'overview' ? 'Resources Center' : state.title}
                                            </li>
                                        </ol>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Render Content */}
                        {renderContent()}
                    </div>
                </div>

                <DashboardFooter />
            </div>
            <Canvas />

            {/* Custom Styles */}
            <style>{`
                .bg-gradient-primary {
                    background: linear-gradient(45deg, #007bff, #0056b3);
                }
                
                .stats-card {
                    transition: transform 0.2s ease-in-out;
                    border-radius: 12px;
                }
                
                .stats-card:hover {
                    transform: translateY(-2px);
                }
                
                .action-card {
                    transition: all 0.2s ease-in-out;
                    border-radius: 12px;
                }
                
                .action-card:hover {
                    transform: translateY(-4px);
                    box-shadow: 0 0.75rem 1.5rem rgba(0, 0, 0, 0.15) !important;
                }
                
                .cursor-pointer {
                    cursor: pointer;
                }
                
                .avatar-sm {
                    width: 2.5rem;
                    height: 2.5rem;
                }
                
                .avatar-md {
                    width: 3.5rem;
                    height: 3.5rem;
                }
                
                .avatar-lg {
                    width: 4.5rem;
                    height: 4.5rem;
                }
                
                .timeline-item:not(:last-child)::after {
                    content: '';
                    position: absolute;
                    left: 1.25rem;
                    top: 3rem;
                    width: 1px;
                    height: calc(100% - 2rem);
                    background-color: #e9ecef;
                }
                
                .timeline-item {
                    position: relative;
                }
            `}</style>
        </>
    );
};

export default ResourcesCenter;
